import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Routes, Route, Link } from 'react-router-dom';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import ProductDetail from '@components/product/ProductDetail';
import SearchResults from '@components/search/SearchResults';
import FeaturedProducts from '@components/pages/FeaturedProducts';
import Cart from '@components/cart/Cart';
import ErrorBoundary from '@components/error/ErrorBoundary';
import ErrorPage from '@components/error/ErrorPage';
import CategoryPage from '@components/pages/CategoryPage';
import ManufacturerPage from '@components/pages/ManufacturerPage';
import { FaqProvider } from '@context/FaqContext';
import { logError } from '@utils/errorLogger';

function App() {
  const [appError, setAppError] = useState(null);

  // Set up global error handling
  useEffect(() => {
    // Handler for uncaught errors
    const handleGlobalError = (event) => {
      event.preventDefault();
      const errorInfo = {
        message: event.error?.message || 'An unexpected error occurred',
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      };
      logError(event.error || new Error(errorInfo.message), {
        componentName: 'Global',
        ...errorInfo,
      });
      setAppError(event.error || new Error(errorInfo.message));
    };

    // Handler for rejected promises
    const handleUnhandledRejection = (event) => {
      event.preventDefault();
      logError(event.reason, {
        componentName: 'Unhandled Promise',
        message: event.reason?.message || 'Unhandled promise rejection',
      });
      setAppError(event.reason || new Error('Unhandled promise rejection'));
    };

    // Handler for component errors from error boundaries
    const handleComponentError = (event) => {
      logError(event.detail.error, event.detail);
      // Don't set app error here as the error boundary will handle it
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('appComponentError', handleComponentError);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
      window.removeEventListener('appComponentError', handleComponentError);
    };
  }, []);

  if (appError) {
    return <ErrorPage error={appError} resetError={() => setAppError(null)} />;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      <ErrorBoundary
        componentName="AppRoot"
        fallbackComponent={(props) => (
          <ErrorPage
            error={props.error}
            resetError={props.resetErrorBoundary}
          />
        )}
        onError={(error, info) =>
          logError(error, {
            componentName: 'AppRoot',
            componentStack: info?.componentStack,
          })
        }
      >
        <FaqProvider>
          <Header />
          <Routes>
            <Route
              path="/"
              element={
                <ErrorBoundary
                  componentName="HomePage"
                  onError={(error, info) =>
                    logError(error, {
                      componentName: 'HomePage',
                      componentStack: info?.componentStack,
                    })
                  }
                >
                  <>
                    {/* Hero Section */}
                    <Box
                      sx={{
                        backgroundColor: '#e1e7f0',
                        py: 6,
                        textAlign: 'center',
                        backgroundImage:
                          'linear-gradient(to right, #e1e7f0, #d1dbe9)',
                      }}
                    >
                      <Container maxWidth="lg">
                        <Typography
                          variant="h4"
                          component="h1"
                          gutterBottom
                          sx={{ fontWeight: 700, color: '#303236' }}
                        >
                          Home Improvement Starts Here
                        </Typography>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ color: '#505259', mb: 4 }}
                        >
                          Shop top brands for kitchen, bath, lighting and more
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' },
                            }}
                            size="large"
                            component={Link}
                            to="/category?Hardware"
                          >
                            Shop Hardware
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' },
                            }}
                            size="large"
                            component={Link}
                            to="/category?Flooring"
                          >
                            Shop Flooring
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' },
                            }}
                            size="large"
                            component={Link}
                            to="/category?Lighting"
                          >
                            Shop Lighting
                          </Button>
                        </Box>
                      </Container>
                    </Box>

                    {/* Featured Products Section */}
                    <ErrorBoundary
                      componentName="FeaturedProducts"
                      onError={(error, info) =>
                        logError(error, {
                          componentName: 'FeaturedProducts',
                          componentStack: info?.componentStack,
                        })
                      }
                    >
                      <React.Suspense fallback={<CircularProgress />}>
                        <FeaturedProducts />
                      </React.Suspense>
                    </ErrorBoundary>
                    <Footer />
                  </>
                </ErrorBoundary>
              }
            />
            <Route
              path="/product/:id"
              element={
                <ErrorBoundary
                  componentName="ProductDetail"
                  onError={(error, info) =>
                    logError(error, {
                      componentName: 'ProductDetail',
                      componentStack: info?.componentStack,
                    })
                  }
                >
                  <ProductDetail />
                </ErrorBoundary>
              }
            />
            <Route
              path="/search"
              element={
                <ErrorBoundary
                  componentName="SearchResults"
                  onError={(error, info) =>
                    logError(error, {
                      componentName: 'SearchResults',
                      componentStack: info?.componentStack,
                    })
                  }
                >
                  <SearchResults />
                </ErrorBoundary>
              }
            />
            <Route
              path="/checkout/cart"
              element={
                <ErrorBoundary
                  componentName="Cart"
                  onError={(error, info) =>
                    logError(error, {
                      componentName: 'Cart',
                      componentStack: info?.componentStack,
                    })
                  }
                >
                  <Cart />
                </ErrorBoundary>
              }
            />
            <Route
              path="/:categoryName"
              element={
                <ErrorBoundary
                  componentName="CategoryPage"
                  onError={(error, info) =>
                    logError(error, {
                      componentName: 'CategoryPage',
                      componentStack: info?.componentStack,
                    })
                  }
                >
                  <CategoryPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/manufacturer/:manufacturerName"
              element={
                <ErrorBoundary
                  componentName="ManufacturerPage"
                  onError={(error, info) =>
                    logError(error, {
                      componentName: 'ManufacturerPage',
                      componentStack: info?.componentStack,
                    })
                  }
                >
                  <ManufacturerPage />
                </ErrorBoundary>
              }
            />
          </Routes>
        </FaqProvider>
      </ErrorBoundary>
    </Box>
  );
}

export default App;
